import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import "./PlanPackages.scss";
import PlanBox from "./PlanBox";
import BookForConsultantPopup from "../../../common/components/book-for-consultant-popup/BookForConsultantPopup";
import PlannerSuccessModal from "../../../common/components/planner-success-modal/PlannerSuccessModal";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { postContinueToOrderPlanner } from "../../../api/order-summary.api";

//  NEW: planner cart APIs
import {
  createPlannerCart,
  getCartCount,
  getCartSummary,
} from "../../../api/cart.api";
import { routes } from "../../../constants/routes";

interface PlansPackagesProps {
  packages: any;
  plannerId: number;
}

const PlansPackages = ({ packages, plannerId }: PlansPackagesProps) => {
  const [openConsultantPopup, setOpenConsultantPopup] =
    useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);

  //  selected plan
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);

  //  snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "success" | "info" | "warning" | "error"
  >("success");
  const [cartPlannerPackages, setCartPlannerPackages] = useState<any>([]);
  // const timeoutRef = useRef<any>(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state: any) => state.session.isLoggedIn);

  const processedPackages = useMemo(() => {
    if (!packages || !Array.isArray(packages)) return [];

    const orderMap: { [key: string]: number } = {
      basic: 1,
      standard: 2,
      premium: 3,
    };

    const sorted = [...packages].sort((a: any, b: any) => {
      const rankA = orderMap[a.name?.toLowerCase()] || 999;
      const rankB = orderMap[b.name?.toLowerCase()] || 999;
      return rankA - rankB;
    });

    return sorted.map((pkg: any) => ({
      ...pkg,
      services: pkg.categories?.map((cat: any) => cat.name) || [],
    }));
  }, [packages]);

  const allServices = useMemo(() => {
    return Array.from(
      new Set(processedPackages.flatMap((pkg: any) => pkg.services)),
    );
  }, [processedPackages]);

  //  keep selection clean if packages change
  useEffect(() => {
    if (
      selectedPlanId &&
      !processedPackages?.some((p: any) => p.id === selectedPlanId)
    ) {
      setSelectedPlanId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processedPackages]);

  const selectedPlan = useMemo(
    () => processedPackages?.find((p: any) => p.id === selectedPlanId) ?? null,
    [processedPackages, selectedPlanId],
  );

  const totalCost = useMemo(() => {
    if (!selectedPlan) return "0.00";
    const n = Number(selectedPlan.price ?? 0);
    if (Number.isNaN(n)) return "0.00";
    return n.toFixed(2);
  }, [selectedPlan]);

const handleAuthRequired = () => {
  const currentPath = window.location.pathname;

  const data = {
    path: currentPath,
    plannerId,
    selectedPlanId,
  };

  localStorage.setItem("planPackage", JSON.stringify(data));
  localStorage.setItem("postLoginRedirect", currentPath);

  // setSnackbarSeverity("info");
  // setSnackbarMsg("Please log in to continue or add plan");
  // setSnackbarOpen(true);

  navigate("/");
};

const getDataFromLocalStorage = () => {
  const data = localStorage.getItem("planPackage");
  if (!data) return;

  try {
    const planPackage = JSON.parse(data);

    // restore only for same planner details page
    if (String(planPackage?.plannerId) !== String(plannerId)) return;

    setSelectedPlanId(planPackage?.selectedPlanId ?? null);
  } catch (error) {
    console.error("Error restoring planPackage from localStorage:", error);
  }
};
  useEffect(() => {
  getDataFromLocalStorage();
}, [plannerId]);

  useEffect(() => {
    if (isLoggedIn) {
      fetchCartSummary();
    }
  }, [isLoggedIn]);
  const handleBookForConsultant = (data: any) => {
    setOpenConsultantPopup(false);
    setShowSuccess(true);
  };

  //  (optional) keep continue for later / wire to your planner order summary flow
  const handleContinue = async () => {
    if (!selectedPlan) return;

    const body = { packageId: selectedPlan.id };

    try {
      const response = await postContinueToOrderPlanner(body);

      // update cart count immediately (same pattern you already use)
      const countResponse = await getCartCount();
      const count = countResponse.data?.data?.data?.totalCount ?? 0;
      dispatch({ type: "CART:SET_COUNT", payload: { count } });

      if (response?.data?.success) {
        // based on your curl response:
        // response.data.data.data.orderId
        const orderId = response?.data?.data?.data?.orderId;
        localStorage.removeItem("planPackage");
        localStorage.removeItem("postLoginRedirect");

        navigate(`/order-summary`, {
          state: {
            orderId,
            plannerId,
            planId: selectedPlan.id,
            planName: selectedPlan.name,
            price: selectedPlan.price,
            isFromPlans: true,
          },
        });
      } else {
        console.error(
          "Failed to continue with planner package",
          response?.data,
        );
      }
    } catch (error) {
      console.error("Error in planner handleContinue:", error);
    }
  };

  const fetchCartSummary = async () => {
    try {
      const response = await getCartSummary();
      const apiData = response?.data?.data?.data;
      const plannerPackages = Array.isArray(apiData.plannerPackages)
        ? apiData.plannerPackages
        : [];
      console.log("partners", plannerPackages);

      if (plannerPackages.length > 0) {
        setCartPlannerPackages(plannerPackages);
      } else {
        setCartPlannerPackages([]);
      }
    } catch (error: any) {
      setCartPlannerPackages([]);
    }
  };

  const checkOderFromAnotherPanner = () => {
    let isSamePartner = true;
    for (let index = 0; index < cartPlannerPackages?.length; index++) {
      if (
        String(cartPlannerPackages[index]?.planner.plannerId) !== String(plannerId)
      ) {
        isSamePartner = false;
        break;
      }
    }
    if (!isSamePartner) {
      setSnackbarOpen(true);
      setSnackbarMsg(
        "You can place an order with only one provider/planner at a time. Please select a single provider/planner to proceed.",
      );
    }
    return isSamePartner;
  };

  /**
   *  NEW: Add Selection -> calls planner cart API (like ServicesAccordion)
   * - createPlannerCart({ packageId })
   * - update count using getPlannerCount()
   * - navigate to /cart with isFromPlans flag
   */
  const handleAddToCart = async () => {
    if (!selectedPlan) {
      console.warn("No plan selected to add to cart");
      return;
    }

    if (!checkOderFromAnotherPanner()) {
      return;
    }

    const isAlreadyInCart = cartPlannerPackages.some(
      (pkg: any) => pkg.package.id === selectedPlan.id,
    );

    if (isAlreadyInCart) {
      setSnackbarSeverity("info");
      setSnackbarOpen(true);
      setSnackbarMsg("This package is already in your selected packages");
      return;
    }

    const body = { packageId: selectedPlan.id };

    try {
      const response = await createPlannerCart(body);

      if (response?.data?.success) {
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setSnackbarMsg("Package added to your selected packages successfully");

        //  Update cart count immediately (planner count)
        const countResponse = await getCartCount();
        const count = countResponse.data?.data?.data?.totalCount ?? 0;

        // Dispatch to Redux (same action as services)
        dispatch({ type: "CART:SET_COUNT", payload: { count } });
        localStorage.removeItem("planPackage");
        localStorage.removeItem("postLoginRedirect");

        //  Navigate to cart (planner mode)
        navigate("/cart", {
          state: {
            plannerId,
            planId: selectedPlan.id,
            isFromPlans: true,
          },
        });
      } else {
        console.error("Failed to add planner package to cart", response?.data);
      }
    } catch (error) {
      console.error("Error adding planner package to cart:", error);
    }
  };

  const anyPlanSelected = !!selectedPlan;

  return (
    <Box className="plans-packages">
      <Typography className="plans-packages__title">
        <span className="plans-packages__title--light">Flexible</span>{" "}
        <span className="plans-packages__title--bold">Plans & Packaging</span>
      </Typography>

      <Typography className="plans-packages__description">
        Choose a plan that fits your needs and budget. Whether you’re looking
        for essential services or a comprehensive package, we provide caring,
        professional support every step of the way.
      </Typography>

      <Box className="plans-packages__boxes">
        {processedPackages.length > 0 ? (
          processedPackages.map((pkg: any) => {
            const isSelected = selectedPlanId === pkg.id;

            return (
              <PlanBox key={pkg.id} pkg={pkg} allServices={allServices}>
                <Button
                  className={`plans-packages__choosePlanBtn ${isSelected ? "selected" : ""
                    }`}
                  onClick={() =>
                    setSelectedPlanId((prev) =>
                      prev === pkg.id ? null : pkg.id,
                    )
                  }
                >
                  {isSelected ? "Selected" : "Choose Plan"}
                </Button>
              </PlanBox>
            );
          })
        ) : (
          <Typography
            className="plans-packages__empty-message"
            sx={{
              textAlign: "center",
              width: "100%",
              paddingY: "3rem",
              color: "#666",
            }}
          >
            There are currently no packages available for this planner. Please
            contact them directly.
          </Typography>
        )}
      </Box>

      {/*  Summary box below plan boxes */}
      {anyPlanSelected && (
        <div className="plans-packages__summary-box">
          <Typography className="plans-packages__summary-title">
            Package Selected
          </Typography>

          <div className="plans-packages__summary-content">
            <div className="plans-packages__summary-list">
              <div className="plans-packages__summary-pill">
                {selectedPlan.name}
              </div>
            </div>

            <div className="plans-packages__summary-right">
              <Typography className="plans-packages__total">
                Total Cost –{" "}
                <span className="plans-packages__total-amount">
                  $ {totalCost.split(".")[0]}
                </span>
              </Typography>

              {totalCost !== "0.00" && (
                <>
                  <Tooltip
                    title={
                      !isLoggedIn
                        ? "Please login to continue with your order"
                        : ""
                    }
                    arrow
                    disableHoverListener={isLoggedIn}
                  >
                    <span>
                      <Button
                        className="plans-packages__btn plans-packages__btn--continue"
                        onClick={
                          isLoggedIn ? handleContinue : handleAuthRequired
                        }
                      >
                        Continue
                      </Button>
                    </span>
                  </Tooltip>

                  <Tooltip
                    title={!isLoggedIn ? "Please login to add selection" : ""}
                    arrow
                    disableHoverListener={isLoggedIn}
                  >
                    <span>
                      <Button
                        className="plans-packages__btn plans-packages__btn--addToCart"
                        onClick={
                          isLoggedIn ? handleAddToCart : handleAuthRequired
                        }
                      >
                        Add Selection
                      </Button>
                    </span>
                  </Tooltip>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <Box className="plans-packages__cta">
        <Typography className="plans-packages__cta-text">
          Not sure what you're looking for? Book a free consultation.
        </Typography>

        <Button
          className="plans-packages__cta-button"
          onClick={() => setOpenConsultantPopup(true)}
        >
          Book a free consultation
        </Button>

        <BookForConsultantPopup
          open={openConsultantPopup}
          onClose={() => setOpenConsultantPopup(false)}
          onSubmit={handleBookForConsultant}
          plannerId={plannerId}
        />
        <PlannerSuccessModal
          open={showSuccess}
          onClose={() => setShowSuccess(false)}
          title="Successful Order"
          subtitle="Thank you"
          businessName={"free consultation"}
          isConsultant={true}
        />
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            "& .MuiAlert-message": {
              padding: "8px 0",
            },
            "& .MuiAlert-icon": {
              display: "flex",
              alignItems: "center",
              padding: 0,
            },
            "& .MuiAlert-action": {
              display: "flex",
              alignItems: "center",
              padding: 0,
            },
          }}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PlansPackages;
